import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styles from './WelcomeNavBar.module.css';
import logo from '../../assets/img/logo.webp';

const SERVICE_LINKS = [
  { label: 'AI Driven Family Discovery', to: '/ai-driven-family-discovery' },
  { label: 'DNA Testing & Matching', to: '/dna-testing-and-matching' },
  { label: 'Family Tree Building', to: '/family-tree-build' },
  { label: 'iGiTREE Connect', to: '/igitree-connect' },
] as const;

const HOME_PATH = '/igisekuruza';

const NAV_LINKS = [
  { label: 'Home', to: HOME_PATH },
  { label: 'About', to: '/about' },
  { label: 'Stories', to: '/user-stories' },
  { label: 'Contact', to: '/contacts' },
  { label: 'Upgrade to igitree +', to: '/upgrade' },
] as const;

function isServiceRoute(pathname: string): boolean {
  return SERVICE_LINKS.some(({ to }) => pathname === to || pathname.startsWith(`${to}/`));
}

function isNavLinkActive(pathname: string, hash: string, to: string): boolean {
  if (to === HOME_PATH) {
    return pathname === HOME_PATH && (hash === '' || hash === '#home');
  }
  if (to.startsWith('/#')) {
    return pathname === HOME_PATH && hash === to.slice(1);
  }
  return pathname === to || pathname.startsWith(`${to}/`);
}

const WelcomeNavBar: React.FC = () => {
  const { pathname, hash } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);

  const onLoginPage = pathname === '/login';
  const onRegisterPage = pathname === '/register';
  const onPasswordRecoveryPage =
    pathname === '/forgot' || pathname === '/reset-password';

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setServicesOpen(false);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setNavScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      className={`${styles.navbar} ${navScrolled ? styles.navbarScrolled : ''}`}
      role="navigation"
      aria-label="Main"
    >
      <Link to="/" className={styles.logo} aria-label="Home" onClick={closeMenu}>
        <img src={logo} alt="" style={{ width: 60, height: 60 }} />
      </Link>

      <ul className={`${styles.navLinks} ${menuOpen ? styles.navLinksOpen : ''}`}>
        {NAV_LINKS.slice(0, 2).map(({ label, to }) => (
          <li key={label}>
            <Link
              to={to}
              className={isNavLinkActive(pathname, hash, to) ? styles.active : ''}
              onClick={() => {
                closeMenu();
                if (to === HOME_PATH && pathname === HOME_PATH) {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
            >
              {label}
            </Link>
          </li>
        ))}
        <li
          className={styles.navDropdown}
        >
          <button
            type="button"
            className={`${styles.navDropdownTrigger} ${isServiceRoute(pathname) ? styles.active : ''}`}
            onClick={() => setServicesOpen((prev) => !prev)}
            aria-expanded={servicesOpen}
            aria-haspopup="menu"
          >
            Services
          </button>
          <div className={`${styles.navDropdownMenu} ${servicesOpen ? styles.navDropdownMenuOpen : ''}`}>
            {SERVICE_LINKS.map(({ label, to }) => (
              <Link
                key={to}
                to={to}
                className={isNavLinkActive(pathname, hash, to) ? styles.active : ''}
                onClick={closeMenu}
                role="menuitem"
              >
                {label}
              </Link>
            ))}
          </div>
        </li>
        {NAV_LINKS.slice(2).map(({ label, to }) => (
          <li key={label}>
            <Link
              to={to}
              className={isNavLinkActive(pathname, hash, to) ? styles.active : ''}
              onClick={() => {
                closeMenu();
                if (to === HOME_PATH && pathname === HOME_PATH) {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
            >
              {label}
            </Link>
          </li>
        ))}
      </ul>

      <div className={styles.navActions}>
        <button
          type="button"
          className={`${styles.menuTrigger} ${menuOpen ? styles.menuTriggerOpen : ''}`}
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-expanded={menuOpen}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
        >
          <span />
          <span />
          <span />
        </button>
        {onLoginPage ? (
          <Link to="/register" className={styles.btnSignin}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
              <path d="M6 12L10 8L6 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Create account
          </Link>
        ) : onRegisterPage || onPasswordRecoveryPage ? (
          <Link to="/login" className={styles.btnSignin}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
              <path d="M6 12L10 8L6 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Sign in
          </Link>
        ) : (
          <Link to="/login" className={styles.btnSignin}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
              <path d="M6 12L10 8L6 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Sign In Now
          </Link>
        )}
      </div>
    </nav>
  );
};

export default WelcomeNavBar;
